
/* eslint-disable no-prototype-builtins */
/* eslint-disable arrow-body-style */
import { useMemo } from "react";
import { merge } from "lodash-es";
import { COMMON_VERTICAL_YAXIS_CONFIG, SCALE_BAISIC_NUMER } from "./constant";
import dataFormat from "@home/utils/dbDataFormat/dataFormat";
import { TDataSourceItem } from "./_type";

/**
 * 从数据源中提取指标值。
 * 
 * @param dataSource - 包含指标数据的数组。
 * @param scaleRate - 缩放比例，当前未使用。
 * @param index - 当前项的索引，当前未使用。
 * @returns 返回一个包含每个项目指标值的数组。
 */
export const extractMetricsValues = (dataSource: TDataSourceItem[]) => {
  return dataSource?.map(item => {
    const values: any[] = [];
    item.metrics?.forEach(metric => {
      values.push(metric?.value);
    });
    return values;
  });
};

/**
 * 从数据源中提取唯一的指标名称。
 * 
 * @param dataSource - 包含多个数据项的数组，每个数据项可能包含多个指标。
 * @returns 返回一个字符串数组，包含所有唯一的指标名称。
 */
const getMetricNames = (dataSource: TDataSourceItem[]): string[] => {
  const metricNamesSet = new Set<string>();
  dataSource.forEach(item => {
    if (item.metrics) {
      item.metrics.forEach(metric => {
        metricNamesSet.add(metric?.key);
      });
    }
  });

  return Array.from(metricNamesSet);
};

/**
 * 自定义 Hook，用于配置 Y 轴的设置。
 * 
 * @param dataSource - 数据源，用于获取指标名称。
 * @returns Y 轴配置对象，包含公共配置和从数据源获取的指标名称。
 */
export const useYAxisConfig = (dataSource: TDataSourceItem[]) => {
  return useMemo(() => {
    return {
      ...COMMON_VERTICAL_YAXIS_CONFIG,
      data: getMetricNames(dataSource)
    };
  }, [dataSource]);
};


/**
 * 计算两组数据的中点数据
 * @param data1 - 第一组数据
 * @param data2 - 第二组数据
 * @returns 包含中点数据的新数组
 */
const getCalculateScaleRateArr = (data1: number[], data2: number[]): number[] => {
  // if (data1?.length !== data2?.length) {
  //   throw new Error("两组数据的长度必须相同");
  // }
  return data1?.map((value, index) => {
    const leftVal = Number(value || 0);
    const rightVal = Number(data2?.[index] || 0) / 2;

    const result =  leftVal + rightVal;
    if(leftVal === 0 && rightVal === 0 ) {
      return 0;
    }
    return SCALE_BAISIC_NUMER / result;
  });
};

/**
 * 格式化数据，将输入的数据数组根据给定的缩放比例进行转换。
 * 
 * @param dataArr - 二维数组，包含需要格式化的数据。
 * @param dataScaleRateArr - 一维数组，包含每个数据项的缩放比例。
 * @param index - 指定要格式化的数据数组的索引。
 * @returns 返回格式化后的数据列表。
 */
const formatData = (dataArr: number[][], dataScaleRateArr: number[], index: string | number | undefined) => {
  const dataList = dataArr?.[index]?.map((item: number, itemIndex: string | number) => {
    return item === '' ? '' : item * dataScaleRateArr?.[itemIndex];
  });
  return dataList;
};


/**
 * @description: 折线图series配置
 * @param params 传入组件的series相关配置: series, symbol, symbolInterval
  * @return {TEchartSeries} series相关配置
 */
export const useLineSeriesConfig = (params: { dataSource: any; series: any;}) => {
  const { dataSource, series } = params;
  const createSeriesConfig = (seriesItem: any, index?: number) => {
    const { name, formatter} = seriesItem || {};
    const dataArr = extractMetricsValues(dataSource);
    const dataScaleRateArr = getCalculateScaleRateArr(dataArr?.[0] || [], dataArr?.[1] || []);
    const dataList = formatData(dataArr,dataScaleRateArr, index);
    const dataListArr= (dataList || dataArr || []);
    return merge({}, {
      name,
      data: dataListArr?.map((item: any, index: string | number) => {
        return {
          value: item,
          scaleRate: dataScaleRateArr?.[index] || 1,
        };
      }),
      type: 'line',
      symbol: 'circle',
      symbolSize: 6,
      z: index === 0 ? 1 : 2,
      label: {
        show: true,
        color: '#262832',
        // padding: [10, 20],
        position: index === 0 ? 'top': 'bottom',
        // align: index === 0 ? 'right' : 'left',
        fontSize: 12,
        z: dataListArr?.length + 1,
        formatter: (params: { data: { value: any; scaleRate: number; }; })=>{
          // return 123456789.1234
          const value = params?.data?.value;
          const scaleRate = params?.data?.scaleRate || 1;
          // 将缩放的值改回去，展示原数据
          return dataFormat({value: value !== '' ? (value / scaleRate): '-', formatRule: formatter });
        },
      }
    }, seriesItem);
  };

  const seriesConfig = useMemo(() => {
    if (Array.isArray(series)) {
      return series.map((item,index)=>createSeriesConfig(item,index));
    }
    return createSeriesConfig(series);
  }, [dataSource, series]);
  return seriesConfig;
};


